import { Card, CardContent, EmptyState } from "@pushpress/pushpress-ui";
import {
  AlertCircle,
  Calendar,
  Flag,
  PartyPopper,
  FileText,
  Receipt,
  UserPlus,
} from "lucide-react";
import ActivityItem from "./ActivityItem";
import { useDashboard } from "../DashboardProvider/DashboardProvider";
import {
  CommunicationsSheetData,
  MemberSheetDataType,
  SheetType,
} from "../DashboardSheetContent/SheetContent";
import { useDashboardSummaryData } from "../../../apis/metricsApi.hooks";
import { useAnalytics } from "../../../components/providers/AnalyticsProvider";
import { useEffect } from "react";

function MyMembers({
  cancelRequestsEnabled,
}: {
  cancelRequestsEnabled: boolean;
}) {
  const { openSheet } = useDashboard();
  const { trackEvent } = useAnalytics();
  const {
    data: dashboardSummaryData,
    isLoading,
    isError,
  } = useDashboardSummaryData();

  useEffect(() => {
    if (isError) {
      trackEvent("Important Activity Error", {
        error: "Error fetching metrics",
      });
    }
  }, [isError, trackEvent]);

  const handleItemClick = ({
    type,
    title,
    data,
    reportUrl,
  }: {
    type: SheetType;
    title: string;
    data: MemberSheetDataType | CommunicationsSheetData;
    reportUrl?: string;
  }) => {
    if (type === "communications") {
      trackEvent("Important Activity Item Clicked", {
        activity_item: title,
        activity_type: type,
      });
      openSheet("communications", title, data, reportUrl);
    } else {
      trackEvent("Important Activity Item Clicked", {
        activity_item: title,
        activity_type: type,
      });
      openSheet("members", title, data, reportUrl);
    }
  };

  return (
    <div className="tw-w-full tw-h-full tw-flex tw-flex-col">
      <h2 className="tw-text-lg tw-font-bold tw-text-gray-700 tw-mb-4">
        Important
      </h2>

      {isError && (
        <Card className="tw-bg-white/75 tw-flex-1 tw-flex tw-items-center tw-justify-center">
          <CardContent>
            <EmptyState
              description="Oops, there was an error fetching metrics"
              icon={<AlertCircle className="tw-h-7 tw-w-7 tw-text-gray-500" />}
              secondaryActionOnClick={() => {
                window.location.reload();
                trackEvent("Important Activity Error Reloaded", {
                  error: "Error fetching metrics",
                  action: "Reload Page",
                });
              }}
              secondaryActionLabel="Reload Page"
            />
          </CardContent>
        </Card>
      )}

      {!isError && (
        <Card className="tw-bg-white/75 tw-flex-1">
          <CardContent className="tw-space-y-4 tw-pt-6">
            <ActivityItem
              icon={<Receipt className="tw-w-5 tw-h-5 tw-text-amber-500" />}
              title="Open invoices"
              count={dashboardSummaryData?.openInvoices?.length ?? 0}
              itemSentiment="negative"
              isLoading={isLoading}
              userInformation={dashboardSummaryData?.openInvoices ?? []}
              onClick={() =>
                handleItemClick({
                  type: "members",
                  title: "Open invoices",
                  data: {
                    type: "openInvoices",
                    data: dashboardSummaryData?.openInvoices ?? [],
                  },
                  reportUrl: "/invoices",
                })
              }
            />
            <ActivityItem
              icon={<AlertCircle className="tw-w-5 tw-h-5 tw-text-amber-500" />}
              title="At Risk"
              subtitle="Last 7 days"
              count={dashboardSummaryData?.atRiskMembers?.length ?? 0}
              itemSentiment="negative"
              isLoading={isLoading}
              userInformation={dashboardSummaryData?.atRiskMembers ?? []}
              onClick={() =>
                handleItemClick({
                  type: "members",
                  title: "At Risk",
                  data: {
                    type: "atRiskMembers",
                    data: dashboardSummaryData?.atRiskMembers ?? [],
                  },
                  reportUrl: "/members/risk",
                })
              }
            />
            <ActivityItem
              icon={<Calendar className="tw-w-5 tw-h-5 tw-text-amber-500" />}
              title="Expiring plans"
              subtitle="Next 30 days"
              count={dashboardSummaryData?.expiringMembers?.length ?? 0}
              itemSentiment="negative"
              isLoading={isLoading}
              userInformation={dashboardSummaryData?.expiringMembers ?? []}
              onClick={() =>
                handleItemClick({
                  type: "members",
                  title: "Expiring plans",
                  data: {
                    type: "expiringMembers",
                    data: dashboardSummaryData?.expiringMembers ?? [],
                  },
                  reportUrl: "/members/expiring",
                })
              }
            />

            {cancelRequestsEnabled && (
              <ActivityItem
                icon={<Flag className="tw-w-5 tw-h-5 tw-text-amber-500" />}
                title="Cancellation requests"
                count={dashboardSummaryData?.cancelRequests?.length ?? 0}
                itemSentiment="negative"
                isLoading={isLoading}
                userInformation={dashboardSummaryData?.cancelRequests ?? []}
                onClick={() =>
                  handleItemClick({
                    type: "members",
                    title: "Cancellation requests",
                    data: {
                      type: "cancelRequests",
                      data: dashboardSummaryData?.cancelRequests ?? [],
                    },
                  })
                }
              />
            )}

            <ActivityItem
              icon={<FileText className="tw-w-5 tw-h-5 tw-text-green-500" />}
              title="New Leads"
              subtitle="Last 7 days"
              count={dashboardSummaryData?.newLeads?.length ?? 0}
              itemSentiment="positive"
              isLoading={isLoading}
              userInformation={dashboardSummaryData?.newLeads ?? []}
              onClick={() =>
                handleItemClick({
                  type: "members",
                  title: "New Leads",
                  data: {
                    type: "newLeads",
                    data: dashboardSummaryData?.newLeads ?? [],
                  },
                  reportUrl: "/people/leads",
                })
              }
            />
            <ActivityItem
              icon={<UserPlus className="tw-w-5 tw-h-5 tw-text-green-500" />}
              title="New memberships"
              subtitle="Last 30 days"
              count={dashboardSummaryData?.newMemberships?.length ?? 0}
              itemSentiment="positive"
              isLoading={isLoading}
              userInformation={dashboardSummaryData?.newMemberships ?? []}
              onClick={() =>
                handleItemClick({
                  type: "members",
                  title: "New memberships",
                  data: {
                    type: "newMemberships",
                    data: dashboardSummaryData?.newMemberships ?? [],
                  },
                  reportUrl: "/reports/subscription_change",
                })
              }
            />
            <ActivityItem
              icon={<PartyPopper className="tw-w-5 tw-h-5 tw-text-green-500" />}
              title="Celebrations"
              subtitle="Next 7 days"
              count={dashboardSummaryData?.celebrations?.length ?? 0}
              itemSentiment="positive"
              isLoading={isLoading}
              userInformation={dashboardSummaryData?.celebrations ?? []}
              onClick={() =>
                handleItemClick({
                  type: "communications",
                  title: "Celebrations",
                  data: {
                    celebrations: dashboardSummaryData?.celebrations ?? [],
                    celebrationsMetadata:
                      dashboardSummaryData?.celebrationsMetadata ?? [],
                  },
                  reportUrl: "/reports/celebrations",
                })
              }
            />
          </CardContent>
        </Card>
      )}
    </div>
  );
}

export default MyMembers;
